import React, { useState } from 'react';
import { FieldValues } from 'react-hook-form';
import {
  Navigate,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { BearerToken } from '@marageti/z4-sdk/lib/auth';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { Traveler } from '@marageti/z4-sdk/lib/people';
import {
  hasAuthorizedRole,
  LocalStorageService,
  useApiClient,
} from '@marageti/z4-lib';
import SetPasswordView from './set-password-view';
import LoginLayout from '../../login/login-layout';
import { useAuthStore } from '../../../store';
import { authorizedRoles, getMatchingUser } from '../../../utils/auth';
import debugPromise from '../../../utils/debug-promise';

const LocalStorage = new LocalStorageService();
const RESET_PATH = '/reset';
const RESET_SEND_EMAIL_PATH = '/reset/send-email';

const buildPath = (path: string, searchParams: URLSearchParams): string => {
  const query = searchParams.toString();
  return query ? `${path}?${query}` : path;
};

const SetPasswordContainer = () => {
  const {
    removeAuthenticatedTraveler,
    setIsAuthenticated,
    setAuthenticatedTraveler,
  } = useAuthStore();
  const apiClient = useApiClient();
  const navigate = useNavigate();

  const tokenExists = useLoaderData();
  const [search, setSearch] = useSearchParams();
  const email = search.get('email');
  const token = search.get('token');
  const redirect = search.get('redirect');

  const [loginError, setLoginError] = useState<ApiError>();
  const tokenExpired = !tokenExists || !email;

  const goToSendEmail = (targetEmail?: string | null) => {
    const params = new URLSearchParams(search);
    params.delete('token');
    if (targetEmail) {
      params.set('email', targetEmail);
      navigate(buildPath(RESET_SEND_EMAIL_PATH, params), { replace: true });
      return;
    }
    params.delete('email');
    navigate(buildPath(RESET_PATH, params), { replace: true });
  };

  const onResetSuccess = (bearerToken: BearerToken) => {
    const { accessToken } = bearerToken;
    if (email) {
      LocalStorage.addToLocalStorage('accessToken', accessToken, (90 * 24 * 60 * 60 * 1000));
      LocalStorage.addToLocalStorage('email', email);
      apiClient.instance.setAuthToken(accessToken);

      return apiClient.peopleClient
        .getTraveler(email)
        .then((data: Traveler) => {
          const matchingUser = getMatchingUser(data.email, data.userInfo);
          if (!matchingUser || !hasAuthorizedRole(matchingUser.roles, authorizedRoles, true)) {
            throw new Error('Not Authorized');
          }
          search.delete('token');
          search.delete('email');
          setSearch(search);
          setAuthenticatedTraveler(data);
          setIsAuthenticated(true);
          if (!redirect) navigate('/');
        })
        .catch((e: ApiError) => {
          debugPromise('setPasswordContainer:getTraveler.catch.removeAuthenticatedTraveler', removeAuthenticatedTraveler());
          setIsAuthenticated(false);
          setLoginError(e);
        });
    }
    return null;
  };

  const onSubmitPassword = ({ password }: FieldValues) => {
    if (email && token) {
      const newPassword = { password };

      return apiClient.authClient
        .putUserResetToken(email, token, newPassword)
        .then(onResetSuccess)
        .catch(() => {
          goToSendEmail(email);
        });
    }
    return null;
  };

  if (tokenExpired) {
    const params = new URLSearchParams(search);
    params.delete('token');
    if (email) {
      params.set('email', email);
      return <Navigate to={buildPath(RESET_SEND_EMAIL_PATH, params)} replace />;
    }
    params.delete('email');
    return <Navigate to={buildPath(RESET_PATH, params)} replace />;
  }

  return (
    <LoginLayout>
      <SetPasswordView
        email={email!}
        loginError={loginError}
        onSubmit={onSubmitPassword}
      />
    </LoginLayout>
  );
};

export default SetPasswordContainer;
